import { useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Chip, InputBase } from '../../base-components';
import { ChipInputWrapper } from './FormChipInput.styles';

interface FormChipInputProps<F extends FieldValues> {
    name: Path<F>;
    control: Control<F>;
    placeholder?: string;
    validate?: (value: string) => boolean;
}


const FormChipInput = <F extends FieldValues>({
    name,
    control,
    placeholder,
    validate,
}: FormChipInputProps<F>) => {
    const [inputValue, setInputValue] = useState('');

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const values: string[] = field.value || [];

                const addValue = (val: string) => {
                    const trimmed = val.trim();
                    if (!trimmed) return;
                    if (validate && !validate(trimmed)) return;
                    if (values.includes(trimmed)) {
                        setInputValue('');
                        return;
                    }
                    field.onChange([...values, trimmed]);
                    setInputValue('');
                };

                const handleKeyDown = (e: React.KeyboardEvent) => {
                    if (
                        e.key === 'Enter' ||
                        e.key === ',' ||
                        e.key === ' '
                    ) {
                        e.preventDefault();
                        addValue(inputValue);
                    }
                    if (
                        e.key === 'Backspace' &&
                        !inputValue &&
                        values.length > 0
                    ) {
                        field.onChange(values.slice(0, -1));
                    }
                };

                const handleDelete = (valToDelete: string) => {
                    field.onChange(
                        values.filter((v) => v !== valToDelete)
                    );
                };

                return (
                    <ChipInputWrapper>
                        {values.map((val) => (
                            <Chip
                                key={val}
                                label={val}
                                onDelete={() => handleDelete(val)}
                                size="small"
                            />
                        ))}
                        <InputBase
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                            }}
                            onKeyDown={handleKeyDown}
                            onBlur={() => {
                                if (inputValue) addValue(inputValue);
                            }}
                            placeholder={placeholder}
                            name={name}
                            autoComplete={name}
                            sx={{ minWidth: 150, flex: 1, fontSize: 14 }}
                        />
                    </ChipInputWrapper>
                );
            }}
        />
    );
};

export default FormChipInput;
